import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { format, isSameDay, startOfMonth } from 'date-fns';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ConditionalWrapper } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import GenericError from '~/components/GenericError/GenericError';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { HistoricAgreementStatsModel } from '~/models';
import { RelationshipProgressStatus } from '~/models/relationship';
import { AgreementStatus } from '~/models/relationship-summary';
import { toDateStampObject } from '~/models/time';
import { selectors } from '~/store/state-selectors';
import { utils } from '~/utils';
import { getLastXMonths } from '~/utils/date.utils';
import { getXAxisTextForQuarterlyDate } from '../charts/charts.utils';
import { commonBarProps, commonCartesianGridProps, commonXAxisProps } from '../charts/commonChartProps';
import CustomBarShape from '../charts/CustomBarShape/CustomBarShape';
import CustomLegend from '../charts/CustomLegend/CustomLegend';
import CustomTooltip from '../charts/CustomTooltip/CustomTooltip';
import CustomXAxisTick from '../charts/CustomXAxisTick/CustomXAxisTick';
import GraphWidgetBottomCard from '../GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '../GraphWidget/GraphWidgetLayout';
import ThreeLineTopCard from '../GraphWidget/ThreeLineTopCard';
import styles from './HistoricAgreementStatsWidget.module.scss';
import { useHistoricAgreementStats } from './hooks/useHistoricAgreementStats';
const agreementsColor = colors.blue700;
const proposalsColor = colors.blue500;
const stackId = 'a';
export const historicAgreementStatsWidgetTextsPath = 'pages.dashboard.historicAgreementStatsWidget';
const getLegendItems = (texts)=>[
        {
            label: texts.activeAgreements(),
            color: agreementsColor
        },
        {
            label: texts.proposalsAwaitingApproval(),
            color: proposalsColor
        }
    ];
const getCard1Props = (texts)=>({
        testId: 'agreements-summary',
        title: texts.activeAgreements(),
        success: true,
        titleInfo: texts.activeAgreementsTooltip(),
        secondaryInfo: texts.activeAgreementsSecondaryTooltip()
    });
const getCard2Props = (texts)=>({
        testId: 'proposals-summary',
        title: texts.awaitingApproval(),
        titleInfo: texts.awaitingApprovalTooltip(),
        secondaryInfo: texts.awaitingApprovalSecondaryTooltip()
    });
export function fillLast12Months(data, now) {
    return getLastXMonths(now, 12).map((date)=>data.find((item)=>isSameDay(startOfMonth(date), startOfMonth(item.date()))) || HistoricAgreementStatsModel.makeEmpty({
            date
        }));
}
function getTooltipItems(payload, texts) {
    if (!payload || !payload.length) return;
    const historicAgreementStatsModel = payload[0].payload;
    return [
        {
            label: texts.proposalsAwaitingApprovalWithCount({
                count: utils.formatNumberWithCommas(historicAgreementStatsModel.proposalsCount())
            }),
            color: proposalsColor
        },
        {
            label: texts.activeAgreementsWithCount({
                count: utils.formatNumberWithCommas(historicAgreementStatsModel.agreementsCount())
            }),
            color: agreementsColor
        }
    ];
}
function getTitle(label, payload) {
    if (!payload || !payload.length) return '';
    const historicAgreementStatsModel = payload[0].payload;
    return format(historicAgreementStatsModel.date(), 'MMMM');
}
function HistoricAgreementStatsChart(param) {
    let { testId, data, isTestMode } = param;
    const { getNow } = useIoContext();
    const { t: widgetTexts } = useLocaleKeys(historicAgreementStatsWidgetTextsPath);
    const barChartProps = isTestMode ? {
        height: 100,
        width: 100
    } : {};
    const fullData = useMemo(()=>fillLast12Months(data, getNow()), [
        data
    ]);
    const legendItems = useMemo(()=>getLegendItems(widgetTexts), []);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testId,
        className: styles.chart,
        children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
            condition: !isTestMode,
            wrapper: (children)=>/*#__PURE__*/ _jsx(ResponsiveContainer, {
                    minHeight: 300,
                    children: children
                }),
            children: /*#__PURE__*/ _jsxs(BarChart, _object_spread_props(_object_spread({
                data: fullData
            }, barChartProps), {
                barCategoryGap: "10%",
                margin: {
                    top: 20,
                    left: 16,
                    right: 16
                },
                children: [
                    /*#__PURE__*/ _jsx(CartesianGrid, _object_spread({}, commonCartesianGridProps)),
                    /*#__PURE__*/ _jsx(Legend, {
                        content: /*#__PURE__*/ _jsx(CustomLegend, {
                            testId: "".concat(testId, "-legend"),
                            items: legendItems
                        })
                    }),
                    /*#__PURE__*/ _jsx(XAxis, _object_spread_props(_object_spread({}, commonXAxisProps), {
                        dataKey: (item)=>item.date(),
                        height: 49,
                        tick: /*#__PURE__*/ _jsx(CustomXAxisTick, {
                            tickHeight: 30,
                            getText: getXAxisTextForQuarterlyDate
                        })
                    })),
                    /*#__PURE__*/ _jsx(YAxis, {
                        axisLine: false,
                        tickLine: false,
                        width: 32,
                        tickFormatter: (value)=>value === 0 ? '' : utils.formatNumberWithCommas(value),
                        fontSize: 12
                    }),
                    /*#__PURE__*/ _jsx(Tooltip, {
                        cursor: false,
                        isAnimationActive: false,
                        content: /*#__PURE__*/ _jsx(CustomTooltip, {
                            skin: "dark",
                            testId: "".concat(testId, "-tooltip"),
                            getTitle: getTitle,
                            getItems: (payload)=>getTooltipItems(payload, widgetTexts)
                        })
                    }),
                    /*#__PURE__*/ _jsx(Bar, _object_spread_props(_object_spread({}, commonBarProps), {
                        dataKey: (item)=>item.agreementsCount(),
                        stackId: stackId,
                        fill: agreementsColor,
                        shape: /*#__PURE__*/ _jsx(CustomBarShape, {
                            getTestId: (item)=>"agreements-for-".concat(toDateStampObject(item.date()).month)
                        })
                    })),
                    /*#__PURE__*/ _jsx(Bar, _object_spread_props(_object_spread({}, commonBarProps), {
                        dataKey: (item)=>item.proposalsCount(),
                        stackId: stackId,
                        fill: proposalsColor,
                        shape: /*#__PURE__*/ _jsx(CustomBarShape, {
                            hasRadius: true,
                            getTestId: (item)=>"proposals-for-".concat(toDateStampObject(item.date()).month)
                        })
                    }))
                ]
            }))
        })
    });
}
function PlaceholderChart(props) {
    const { getNow } = useIoContext();
    const placeholderData = useMemo(()=>{
        const emptyData = fillLast12Months([], getNow());
        return emptyData.map((item, idx)=>item.setActiveAgreementsCount(Math.floor(idx / 2) + 3).setProposalsCount(3));
    }, []);
    return /*#__PURE__*/ _jsx(HistoricAgreementStatsChart, _object_spread({
        testId: "agreement-stats-chart-placeholder",
        data: placeholderData
    }, props));
}
const Layout = (param)=>{
    let { loading, children } = param;
    const { t: historicAgreementStatsWidgetTexts } = useLocaleKeys('pages.dashboard.historicAgreementStatsWidget');
    return /*#__PURE__*/ _jsx(GraphWidgetLayout, {
        title: historicAgreementStatsWidgetTexts.title(),
        loading: loading,
        bottomHeight: "l",
        children: children
    });
};
export default function HistoricAgreementStatsWidget(props) {
    const { t: widgetTexts } = useLocaleKeys(historicAgreementStatsWidgetTextsPath);
    const card1Props = useMemo(()=>getCard1Props(widgetTexts), []);
    const card2Props = useMemo(()=>getCard2Props(widgetTexts), []);
    const { historicAgreementStats, isLoading, isFetching, error } = useHistoricAgreementStats({
        refreshOnHookMount: true
    });
    const hasSentAProposal = useStore(selectors.hasSentAProposal);
    const isMemberUser = useStore(selectors.user.isMemberUser);
    const loadingOrEmptyBg = ()=>/*#__PURE__*/ _jsx(PlaceholderChart, _object_spread({}, props));
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: "widget-error"
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(Layout, {
            loading: true,
            children: [
                /*#__PURE__*/ _jsx(ThreeLineTopCard, _object_spread_props(_object_spread({}, card1Props), {
                    loading: true
                })),
                /*#__PURE__*/ _jsx(ThreeLineTopCard, _object_spread_props(_object_spread({}, card2Props), {
                    loading: true
                })),
                /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                    loading: true,
                    loadingMsg: widgetTexts.loadingMsg(),
                    loadingOrEmptyBg: loadingOrEmptyBg
                })
            ]
        });
    }
    const isEmpty = historicAgreementStats.length === 0;
    const firstItem = isEmpty ? HistoricAgreementStatsModel.makeEmpty() : historicAgreementStats[0];
    return /*#__PURE__*/ _jsxs(Layout, {
        loading: isFetching,
        children: [
            /*#__PURE__*/ _jsx(ThreeLineTopCard, _object_spread_props(_object_spread({}, card1Props), {
                primaryText: firstItem.agreementsCount(),
                secondaryText: isMemberUser ? undefined : widgetTexts.monthlyRecurring({
                    amount: firstItem.agreementsDollarAmount()
                }),
                linkTo: "".concat(routes.agreementsDashboard, "?agreementStatus=").concat(AgreementStatus.Active)
            })),
            /*#__PURE__*/ _jsx(ThreeLineTopCard, _object_spread_props(_object_spread({}, card2Props), {
                primaryText: firstItem.proposalsCount(),
                secondaryText: isMemberUser ? undefined : widgetTexts.monthlyRecurring({
                    amount: firstItem.proposalsDollarAmount()
                }),
                linkTo: "".concat(routes.proposalsDashboard, "?filter=").concat(RelationshipProgressStatus.PaymentMethod, ",").concat(RelationshipProgressStatus.Reviewed, ",").concat(RelationshipProgressStatus.Sent)
            })),
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                empty: isEmpty && !hasSentAProposal,
                emptyMsg: widgetTexts.emptyMsg(),
                testId: "agreement-stats",
                loadingOrEmptyBg: loadingOrEmptyBg,
                children: /*#__PURE__*/ _jsx(HistoricAgreementStatsChart, _object_spread({
                    testId: "agreement-stats-chart",
                    data: historicAgreementStats
                }, props))
            })
        ]
    });
}
